import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from "@angular/forms";
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { Subscription } from 'rxjs';
import { Borrower } from '../../../models/borrower-model';
import { Employment } from '../../../models/employment-model';
import { TypeOfIncome } from '../../../models/enums/type-of-income.enum';
import { EnumsService } from '../../../services/enums.service';
import { AddressComponent, AddressFieldConfigSettings } from '../../address/address.component';
import { BorrowerPicker } from '../../borrower-picker/borrower-picker.component';
import { ConfigurableFieldHostComponent } from '../../configurable-field-host.component';
import { Constants } from '../../../services/constants';
import { Address } from '../../../models/address-model';
import { AddressSelectedEvent } from 'projects/shared/address-autocomplete-v2/address-autocomplete-v2.component';
import { isEmploymentEndDateInFuture } from '../../../utils/employment-duration.utils';

declare const $: any;

const soleProprietorOwnershipSharePercent = 100;

@Component({
    selector: 'employment-dialog',
    templateUrl: 'employment-dialog.component.html',
    styleUrls: ['./employment-dialog.component.scss'],
    standalone: false
})
export class EmploymentDialogComponent
  extends ConfigurableFieldHostComponent
  implements OnInit, AfterViewInit, OnDestroy {

  private readonly _transloco = inject(TranslocoService);

  @ViewChild('employmentInfoForm')
  employmentInfoForm: NgForm | undefined;

  @ViewChild(AddressComponent)
  addressComponent: AddressComponent | undefined;

  @ViewChild('borrowerPicker')
  borrowerPicker: BorrowerPicker | undefined;

  inEditMode: boolean = false;

  @Input()
  set employment(employment: Employment) {
    this._employment = employment;
    this.inEditMode = true;
    // Provenance does not survive saving and re-opening - the parent builds a fresh dialog, so the
    // flag below starts false and the 100% would look like the borrower's own answer. A saved Sole
    // Proprietor sitting at exactly the default is therefore read back as defaulted, so switching
    // business type drops it just as it would in the dialog that created it. The persisted record
    // carries no default/manual provenance of its own, so a borrower who typed 100 on a sole
    // proprietorship is indistinguishable from the default and is treated the same way.
    // This only restores dialog state: saved percentages, including older blanks, stay untouched.
    this._ownershipSharePercentDefaulted = this.isSoleProprietor &&
      this.ownershipSharePercent === soleProprietorOwnershipSharePercent;
    // A deferred derivation belongs to the employment that asked for it, so it does not follow the
    // dialog onto a different record. Today's callers assign once, but the input does not promise that.
    this._ownershipShareDerivePending = false;
  }

  get employment(): Employment {
    return this._employment;
  }

  @Input() borrowers: Borrower[] = [];

  @Input()
  typeOfIncome: TypeOfIncome;

  @Input()
  showCountry: boolean = false;

  selectedBorrowerId: number | undefined;

  integerPattern = {
    X: { pattern: new RegExp('[1-9]') },
    Y: { pattern: new RegExp('[0-9]') },
  };

  onEmployerSelected = (e: AddressSelectedEvent) => {
    // employer field shows only business name when present
    this.employment.employer = e.businessName && e.businessName.trim().length > 0 ? e.businessName : e.streetAddress;
    const address = new Address();
    address.address1 = e.streetAddress || '';
    address.city = e.city || '';
    const st = this._enumsService.states.find(s => s.name?.toUpperCase() === e.state?.toUpperCase());
    address.state = st ? st.value : e.state || '';
    address.zipCode = e.zipCode || '';
    this.employment.address = address;
  }

  businessTypes: EnumerationItem[] = [];

  ownershipShareTypes: EnumerationItem[] = [];

  militaryPayType: string | null = null;

  options: any = {
    types: ['establishment'],
    componentRestrictions: { country: 'us' },
  };

  isEndDateMessageShowing: boolean = false;
  isEndDateInFutureMessageShowing: boolean = false;

  isRetired: boolean = false;

  protected selfEmploymentForms: EnumerationItem[] = [];

  protected ownerShipShareTypeGreaterOrEqualTo25Percent: string | undefined;

  protected ownerShipShareTypeLessThan25Percent: string | undefined;

  protected employmentAddressFieldConfigSettings: AddressFieldConfigSettings;

  protected doesNotKnowOwnershipSharePercent: boolean = false;

  // The 100% in the model is the sole proprietor default rather than a figure the borrower gave us,
  // so it is dropped if they move to a business type that may have co-owners.
  private _ownershipSharePercentDefaulted: boolean = false;

  // The default's bucket needs an enumeration value. When Sole Proprietor is picked before the
  // enumerations arrive, deriving it is deferred to the subscription in the constructor.
  private _ownershipShareDerivePending: boolean = false;

  private _jobTypes: any[] = [];

  private _subscriptionToBorrowerChanged: Subscription;

  private _employment: Employment;

  private _allJobTypes = [
    { labelKey: 'wizard.employment.primaryEmployment', value: 'true' },
    { labelKey: 'wizard.employment.secondaryEmployment', value: 'false' },
  ];

  private _onlySecondaryJobType = [
    { labelKey: 'wizard.employment.secondaryEmployment', value: 'false' },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private readonly _enumsService: EnumsService
  ) {
    super();
    this._employment = new Employment();
    this._employment.employmentType = 'CurrentEmployer';
    this.typeOfIncome = TypeOfIncome.Base;
    this._jobTypes = this._allJobTypes;
    this.businessTypes = this._enumsService.businessTypes;

    this._enumsService.getMortgageEnumerations().subscribe((enums) => {
      this.ownershipShareTypes = this._enumsService.translateEnumArray(
        enums[Constants.enumerations.employmentOwnershipShare], 'ownershipShare');
      this.selfEmploymentForms = this._enumsService.translateEnumArray(
        enums[Constants.enumerations.selfEmploymentForm], 'selfEmploymentForm');

      this.ownerShipShareTypeGreaterOrEqualTo25Percent = this._enumsService.getEnumValue(Constants.enumerationValueNames.EmploymentOwnerShipShare.GreaterOrEqualTo25Percent);
      this.ownerShipShareTypeLessThan25Percent = this._enumsService.getEnumValue(Constants.enumerationValueNames.EmploymentOwnerShipShare.LessThan25Percent);

      // The enumerations are normally cached by the income step and resolve synchronously here. When
      // they are not, the borrower may pick Sole Proprietor before they arrive: the 100% default is
      // applied at once but its bucket needs an enumeration value, so it is derived here on arrival.
      // The second clause is the older case of a percentage that reached us with no bucket at all.
      // Neither fires for a saved entry that already has both, which is left exactly as saved.
      if (this._ownershipShareDerivePending ||
        (this.ownershipSharePercent !== null && !this.employment.borrowerOwnershipShare)) {
        this._ownershipShareDerivePending = false;
        this.deriveOwnershipShareFromPercent();
      }
    });
  }

  // A sole proprietor owns the whole business, so the borrower is not asked to figure the
  // percentage out: it is defaulted to 100%, which derives the >=25% ownership share bucket.
  onBusinessTypeChanged = (): void => {
    if (!this.isSoleProprietor) {
      // Never leave the 100% default behind on a business the borrower may share with co-owners;
      // a percentage they typed themselves is theirs and stays. The bucket goes with the default
      // that derived it: left behind, the entry would read as a >=25% owner on a percentage nobody
      // asserted, which is what the self-employed document tasks gate on.
      if (this._ownershipSharePercentDefaulted) {
        this.employment.borrowerOwnershipSharePercent = undefined;
        // Only the bucket the default itself derives goes with it. Any other bucket is one the
        // borrower picked - it is still sitting there when the enumerations were too slow for the
        // default to overwrite it - and dropping that would throw away their own answer.
        if (this.employment.borrowerOwnershipShare === this.ownerShipShareTypeGreaterOrEqualTo25Percent) {
          this.employment.borrowerOwnershipShare = undefined;
        }
        this._ownershipSharePercentDefaulted = false;
        this._ownershipShareDerivePending = false;
      }
      return;
    }
    this.applySoleProprietorOwnershipDefaults();
  }

  // Borrowers who share a business with other owners often cannot state their exact percentage.
  // They can say so and pick the ownership share bucket instead, which is what the URLA needs.
  onDoesNotKnowOwnershipSharePercentChanged = (): void => {
    if (this.doesNotKnowOwnershipSharePercent) {
      this.employment.borrowerOwnershipSharePercent = undefined;
    }
  }

  // The exact ownership percentage drives the categorical ownership share bucket,
  // which the borrower-characteristic task logic relies on and which survives LOS sync.
  onOwnershipSharePercentChanged = (): void => {
    // Anything that reaches the model through the input is the borrower's own answer, never the
    // sole proprietor default (ngx-mask does not emit on programmatic numeric writes).
    this._ownershipSharePercentDefaulted = false;
    this.deriveOwnershipShareFromPercent();
  }

  private deriveOwnershipShareFromPercent = (): void => {
    // ngx-mask writes '' (never null) into the model - both when the field is cleared and when a
    // null model is first written to the input as the dialog opens - so coerce rather than compare
    // against null/undefined. '' >= 25 would otherwise evaluate 0 >= 25 and silently stamp
    // "less than 25%" over an existing >=25% owner, which is the LLS-369 symptom itself.
    const percent = this.ownershipSharePercent;
    if (percent === null) {
      // No exact percentage: leave any existing categorical value alone. This matches the Api,
      // where ToOwnershipShare(null) returns null and callers fall back with
      // `?? em.BorrowerOwnershipShare` to preserve what is already there.
      return;
    }
    this.employment.borrowerOwnershipShare = percent >= 25
      ? this.ownerShipShareTypeGreaterOrEqualTo25Percent
      : this.ownerShipShareTypeLessThan25Percent;
  }

  ngOnInit() {
    if ((!this.inEditMode && !this.isMilitaryRelated) || this.inEditMode) {
      this.militaryPayType = 'Employment';
    }
    // Only default borrowerId for brand-new (add-mode) employments. In edit
    // mode a client-side-only employment still has employmentId = 0, so the
    // legacy check would re-default it and clobber the picker's selected
    // borrower on every re-open.
    if (!this.inEditMode && !this._employment.borrowerId && this.borrowers.length > 0) {
      this._employment.borrowerId = this.borrowers[0].borrowerId;
    }
    $('.modal-body').scroll(function () {
      //Set new top to autocomplete dropdown
      const newTop = $('#employer').offset().top + $('#employer').outerHeight();
      $('.pac-container').css('top', newTop + 'px');
    });

    this.isRetired = this.employment.employer && this.employment.employer == "Retired" ? true : false;

    // An entry saved with only the categorical share (no exact percentage) is one where the
    // borrower did not know it, so re-open it in that state instead of demanding a percentage.
    this.doesNotKnowOwnershipSharePercent = this.inEditMode && !this.isSoleProprietor &&
      this.ownershipSharePercent === null && !!this.employment.borrowerOwnershipShare;

    // Deliberately no sole proprietor default here: a saved entry is re-opened exactly as it was
    // saved, blank percentage included. The 100% default is for entries being created - it is
    // applied when the borrower picks Sole Proprietor (onBusinessTypeChanged), never on re-open.

    this.employmentAddressFieldConfigSettings = {
      stateField: 'employment.employer.address.state',
      addressField: 'employment.employer.address.address1',
      cityField: 'employment.employer.address.city',
      zipCodeField: 'employment.employer.address.zipCode',
      countyField: 'employment.employer.address.county',
      countryField: 'employment.employer.address.country'
    }
  }

  ngAfterViewInit(): void {
    if (this.borrowerPicker) {
      this._subscriptionToBorrowerChanged =
        this.borrowerPicker.borrowerChangedEmitter.subscribe((e: any) =>
          this.onBorrowerChanged(e)
        );
    }
  }

  ngOnDestroy(): void {
    if (this._subscriptionToBorrowerChanged) {
      this._subscriptionToBorrowerChanged.unsubscribe();
    }
  }

  isRetiredChanged = () => {
    if (this.isRetired) {
      this.employment.employer = "Retired";
    }
    else {
      if (this.employment.employer && this.employment.employer == "Retired") {
        this.employment.employer = null;
      }
    }
  }

  public get header(): string {
    if (this.inEditMode) {
      return this._transloco.translate('wizard.employment.editing') + ' ' + this._employment.employer;
    }
    if (this.typeOfIncome === TypeOfIncome.MilitaryBasePay) {
      return this._transloco.translate('wizard.employment.addMilitaryPay');
    } else if (this.typeOfIncome === TypeOfIncome.ContractBasis) {
      return this._transloco.translate('wizard.employment.addIndependentContractor');
    } else if (this.typeOfIncome === TypeOfIncome.SelfEmploymentIncome) {
      return this._transloco.translate('wizard.employment.addBusinessSelfEmployment');
    }
    return this._transloco.translate('wizard.employment.addEmployment');
  }

  public get jobTypes() {
    return this._jobTypes;
  }

  handleAddressChange = (e: any) => {
    const address = new Address();
    const employer = e.name;
    e.address_components.forEach(
      (component: { types: any; long_name: any; short_name: any }) => {
        const types = component.types;
        types.forEach((type: string) => {
          if (type === 'locality' || type === 'sublocality') {
            address.city = component.long_name;
          }
          if (type === 'administrative_area_level_1') {
            const state = this._enumsService.states.find(
              (s) => s.name === component.short_name
            );
            if (state) {
              address.state = state.value;
            }
          }
          if (type === 'postal_code') {
            address.zipCode = component.long_name;
          }
          if (type === 'street_number') {
            address.address1 = component.short_name;
          }
          if (type === 'route') {
            address.address1 += ' ' + component.short_name;
          }
        });
      }
    );
    this.employment.employer = "...";
    setTimeout(() => {
      this.employment.employer = employer;
      this.employment.address = address;
    });
  };

  onCountryChanged = (country: string) => {
    if(country && country != "us"){
      this.employment.employer = "";
    }
  }

  onSaveClicked = () => {
    this.borrowerPicker?.markAsTouched();
    this.employmentInfoForm?.form.markAllAsTouched();
    let valid: boolean | undefined = true;
    valid = this.employmentInfoForm?.form.valid && valid;
    if (this.addressComponent) {
      valid = this.addressComponent.validate() && valid;
    }

    // start/end date controls
    this.isEndDateMessageShowing = false;
    this.isEndDateInFutureMessageShowing = false;
    if (!this.isHidden('employment.startDate') && this.isPrevious && !this.isHidden('employment.endDate')) {
      if (new Date(this.employment.startDate) > new Date(this.employment.endDate)) {
        this.isEndDateMessageShowing = true;
        valid = false;
        return;
      }
    }
    if (this.isPrevious && !this.isHidden('employment.endDate') && isEmploymentEndDateInFuture(this.employment.endDate)) {
      this.isEndDateInFutureMessageShowing = true;
      return;
    }

    if (!valid) {
      return;
    }

    let typeOfIncome = this.typeOfIncome;
    if (this.militaryPayType && this.militaryPayType === 'Disability') {
      typeOfIncome = TypeOfIncome.Disability;
    }
    const result: EmploymentAndIncomeType = {
      employment: this._employment,
      typeOfIncome: typeOfIncome,
    };
    this.activeModal.close(result);
  };

  public get isCurrent(): boolean {
    return this.employment?.employmentType == 'CurrentEmployer';
  }

  public get isPrevious(): boolean {
    return this.employment?.employmentType == 'FormerEmployer';
  }

  public get isIndependentContractor(): boolean {
    return this.typeOfIncome === TypeOfIncome.ContractBasis;
  }

  public get isMilitaryRelated(): boolean {
    return this.typeOfIncome === TypeOfIncome.MilitaryBasePay;
  }

  public get isBusinessSelfEmployment(): boolean {
    return this.typeOfIncome === TypeOfIncome.SelfEmploymentIncome ||
      this.employment?.selfEmployed;
  }

  protected get isSoleProprietor(): boolean {
    return this.employment?.businessType === Constants.enumerationValueNames.BusinessType.SoleProprietor;
  }

  // The percentage is what the sole proprietor defaults are captured in, so it is shown for a
  // sole proprietor even where the flow configuration hides it for other business types.
  protected get isOwnershipSharePercentVisible(): boolean {
    return this.isSoleProprietor || !this.isHidden('employment.borrowerOwnershipSharePercent');
  }

  // Cleared out when the borrower says they do not know it, and then the categorical share is
  // theirs to pick instead of being derived.
  protected get isOwnershipSharePercentCaptured(): boolean {
    return this.isOwnershipSharePercentVisible && !this.doesNotKnowOwnershipSharePercent;
  }

  private get ownershipSharePercent(): number | null {
    const rawPercent = this.employment.borrowerOwnershipSharePercent;
    const percent = typeof rawPercent === 'number' ? rawPercent : parseFloat(rawPercent as any);
    return Number.isFinite(percent) ? percent : null;
  }

  private applySoleProprietorOwnershipDefaults = (): void => {
    if (!this.isSoleProprietor) {
      return;
    }
    this.doesNotKnowOwnershipSharePercent = false;
    this.employment.borrowerOwnershipSharePercent = soleProprietorOwnershipSharePercent;
    // The bucket needs the enumeration value; if it is not here yet the subscription above derives
    // it on arrival, which is what the pending flag is for.
    if (this.ownerShipShareTypeGreaterOrEqualTo25Percent) {
      this.deriveOwnershipShareFromPercent();
    } else {
      this._ownershipShareDerivePending = true;
    }
    this._ownershipSharePercentDefaulted = true;
  }

  private onBorrowerChanged = (borrowerId: any): void => {
    const borrower = this.borrowers.find((b) => b.borrowerId === borrowerId);
    if (borrower?.employments.find((e) => e.isPrimary)) {
      this._jobTypes = this._onlySecondaryJobType;
      this.employment.isPrimary = 'false';
    } else {
      this._jobTypes = this._allJobTypes;
      this.employment.isPrimary = 'true';
    }
  };
}

export class EmploymentAndIncomeType {
  employment: Employment;
  typeOfIncome: TypeOfIncome;
}
